/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1752019200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jul 09 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "adobe-target",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to Adobe Target",
          "depth": 0,
          "id": "connect-to-adobe-target"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing profiles",
          "depth": 1,
          "id": "syncing-profiles"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ol: "ol",
      li: "li",
      ul: "ul",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {PremiumDestination, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Adobe Target"
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Profiles"
              })
            }), _jsx(_components.td, {
              children: "Create and update visitor profile attributes"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://experienceleague.adobe.com/en/docs/target-dev/developer/api/profile-apis/profile-bulk-api",
                children: "Profiles"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "To generate the required authentication credentials to connect Adobe Target to Hightouch, follow these instructions:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to your ", _jsx(_components.a, {
            href: "https://developer.adobe.com/console/home",
            children: "Adobe admin console"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create new project"
          }), " > ", _jsx(_components.strong, {
            children: "Add API"
          }), " > ", _jsx(_components.strong, {
            children: "Adobe Target"
          }), " and click ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "OAuth Server-to-Server"
          }), " authentication method."]
        }), "\n", _jsx(_components.li, {
          children: "Once the authentication method has been created, select the credentials."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Retrieve client secret"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-adobe-target",
        children: "Connect to Adobe Target"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button.\nSelect ", _jsx(_components.strong, {
          children: "Adobe Target"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ".\nYou can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Adobe Target"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client code"
          }), ": Your client code can be found in the ", _jsx(_components.a, {
            href: "https://experience.adobe.com/",
            children: "Adobe Experience Cloud"
          }), " under ", _jsx(_components.strong, {
            children: "Target"
          }), " > ", _jsx(_components.strong, {
            children: "Administration"
          }), " > ", _jsx(_components.strong, {
            children: "Implementation"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tenant name"
          }), ": Tenant name can be found in the URL when you log into Experience Cloud (", _jsx(_components.code, {
            children: "https://experience.adobe.com/#/@<TENANT_NAME>/target/setup/implementation"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client ID"
          }), ": This is created along with the client secret from the steps above"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client secret"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Make sure to access the ", _jsx(_components.strong, {
            children: "Profile API"
          }), " slide in your Adobe Target settings and enable the ", _jsx(_components.strong, {
            children: "Require Authentication"
          }), " toggle.\nThen click ", _jsx(_components.strong, {
            children: "Generate New Profile Authentication Token"
          }), ".\nYou don't need to use the profile authentication token when setting up an Adobe Target destination in Hightouch, but generating a new token is required by Adobe.\nYou can find more details about this in ", _jsx(_components.a, {
            href: "https://experienceleague.adobe.com/docs/target-dev/developer/implementation/methods/profile-api-settings.html",
            children: "Adobe Target's documentation"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Adobe Target destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Adobe Target destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-profiles",
        children: "Syncing profiles"
      }), "\n", _jsxs(_components.p, {
        children: ["Syncing to Adobe Target's ", _jsx(_components.a, {
          href: "https://experienceleague.adobe.com/en/docs/target-dev/developer/api/profile-apis/profile-bulk-api",
          children: "Bulk Profile API"
        }), " lets you manage visitor profile attributes with data typically stored in cookies or third-party apps."]
      }), "\n", _jsx(_components.h4, {
        id: "syncing-to-audiences",
        children: "Syncing to audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Audiences in Adobe Target are condition-based, so syncing profiles to audiences is handled by setting a ", _jsx(_components.code, {
          children: "__hightouch_audience_{model name}"
        }), " attribute to match on. You can only choose to create a new audience, and the condition of the audience will be set to include all user profiles whose ", _jsx(_components.code, {
          children: "__hightouch_audience_{model name}"
        }), " attribute is ", _jsx(_components.code, {
          children: "true"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to profiles in Adobe Target, you need to select a model column and corresponding Adobe Target field. You can match on any of the following Adobe Target fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "thirdPartyId (recommended)"
        }), "\n", _jsx(_components.li, {
          children: "pcID"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in Adobe Target. Ensure the data type of your model column matches the data type of the field you want to sync to."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the profile in Target"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Keep the profile in Target and clear fields managed by Hightouch"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h4, {
        id: "non-unique-ids",
        children: "Non-unique IDs"
      }), "\n", _jsx(_components.p, {
        children: "Profiles can be matched from your warehouse to your Adobe Target destination using the pcID or a third party ID. If you selected a model column that isn't unique, the incorrect profile may be updated."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "failed-to-authenticate-to-adobe-target-400---invalid-request-authentication-is-disabled-for-current-client",
        children: "Failed to authenticate to Adobe Target: 400 - Invalid request: authentication is disabled for current client"
      }), "\n", _jsxs(_components.p, {
        children: ["As explained in the ", _jsx(_components.a, {
          href: "#connect-to-adobe-target",
          children: "Connect to Adobe Target"
        }), " section, make sure to access the ", _jsx(_components.strong, {
          children: "Profile API"
        }), " slide in your Adobe Target settings and enable the ", _jsx(_components.strong, {
          children: "Require Authentication"
        }), " toggle.\nThen click ", _jsx(_components.strong, {
          children: "Generate New Profile Authentication Token"
        }), ".\nYou don't need to use the profile authentication token when setting up an Adobe Target destination in Hightouch, but generating a new token is required by Adobe.\nYou can find more details about this in ", _jsx(_components.a, {
          href: "https://experienceleague.adobe.com/docs/target-dev/developer/implementation/methods/profile-api-settings.html",
          children: "Adobe Target's documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
